import { formatAddressParts, formatAddressText } from "@/lib/address-format";
import { type LatLng, distanceMeters, isWithinMeters } from "@/lib/geo";
import type { PhotoAnswer } from "@/lib/photo-answer";

/** Photos within this distance of the property attach to the property pin. */
export const PHOTO_NEAR_PROPERTY_METERS = 100;

/** Photos within this distance of each other form one off-property group pin. */
export const PHOTO_GROUP_PROXIMITY_METERS = 100;

export type PhotoMapPinKind = "property" | "photo_group";

export type PhotoMapPin = {
  id: string;
  kind: PhotoMapPinKind;
  latitude: number;
  longitude: number;
  photos: PhotoAnswer[];
  /** Human-readable popup body (count + time range). */
  popupText: string;
};

export type PhotoTimeRange = {
  earliest: Date;
  latest: Date;
  /** IANA timezone used for display (first non-empty among photos). */
  timezone: string;
};

export function photosWithCoordinates(photos: PhotoAnswer[]): PhotoAnswer[] {
  return photos.filter(
    (p) =>
      Number.isFinite(p.latitude) &&
      Number.isFinite(p.longitude) &&
      typeof p.captured_at === "string" &&
      p.captured_at.length > 0,
  );
}

/**
 * Earliest/latest capture times among photos. Returns null when no valid
 * timestamps exist. Out-of-order input is fine.
 */
export function computePhotoTimeRange(
  photos: PhotoAnswer[],
): PhotoTimeRange | null {
  let earliest: Date | null = null;
  let latest: Date | null = null;
  let timezone = "";

  for (const photo of photos) {
    const t = Date.parse(photo.captured_at);
    if (!Number.isFinite(t)) continue;
    const d = new Date(t);
    if (!earliest || d < earliest) earliest = d;
    if (!latest || d > latest) latest = d;
    if (!timezone && photo.timezone?.trim()) {
      timezone = photo.timezone.trim();
    }
  }

  if (!earliest || !latest) return null;
  return { earliest, latest, timezone: timezone || "UTC" };
}

function formatTimeInZone(date: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }
}

function formatDateInZone(date: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  }
}

function sameCalendarDay(a: Date, b: Date, timeZone: string): boolean {
  return formatDateInZone(a, timeZone) === formatDateInZone(b, timeZone);
}

/**
 * Format a capture time range for pin popups, e.g.:
 * - single: "Mar 1, 2026, 10:30 AM"
 * - same day: "Mar 1, 2026, 10:30–10:45 AM"
 * - multi-day: "Mar 1, 10:30 AM – Mar 2, 9:00 AM"
 * Appends timezone abbreviation when available.
 */
export function formatPhotoTimeRange(photos: PhotoAnswer[]): string | null {
  const range = computePhotoTimeRange(photos);
  if (!range) return null;

  const { earliest, latest, timezone } = range;
  const tzLabel = formatTimezoneAbbreviation(latest, timezone);
  const suffix = tzLabel ? ` ${tzLabel}` : "";

  if (earliest.getTime() === latest.getTime()) {
    return `${formatDateInZone(earliest, timezone)}, ${formatTimeInZone(earliest, timezone)}${suffix}`;
  }

  if (sameCalendarDay(earliest, latest, timezone)) {
    return `${formatDateInZone(earliest, timezone)}, ${formatTimeInZone(earliest, timezone)}–${formatTimeInZone(latest, timezone)}${suffix}`;
  }

  return `${formatDateInZone(earliest, timezone)}, ${formatTimeInZone(earliest, timezone)} – ${formatDateInZone(latest, timezone)}, ${formatTimeInZone(latest, timezone)}${suffix}`;
}

function formatTimezoneAbbreviation(date: Date, timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/** Popup line: "3 photos · Mar 1, 2026, 10:30–10:45 AM PDT" */
export function formatPhotoPinPopup(photos: PhotoAnswer[]): string {
  const count = photos.length;
  const countLabel = count === 1 ? "1 photo" : `${count} photos`;
  const timeRange = formatPhotoTimeRange(photos);
  return timeRange ? `${countLabel} · ${timeRange}` : countLabel;
}

function groupCentroid(photos: PhotoAnswer[]): LatLng {
  const lat =
    photos.reduce((sum, p) => sum + p.latitude, 0) / Math.max(photos.length, 1);
  const lng =
    photos.reduce((sum, p) => sum + p.longitude, 0) /
    Math.max(photos.length, 1);
  return { latitude: lat, longitude: lng };
}

/**
 * Connected-components clustering: photos within `thresholdMeters` of each
 * other (pairwise) share a group. Deterministic by token then captured_at.
 */
export function clusterPhotosByProximity(
  photos: PhotoAnswer[],
  thresholdMeters: number = PHOTO_GROUP_PROXIMITY_METERS,
): PhotoAnswer[][] {
  const items = photosWithCoordinates(photos)
    .slice()
    .sort((a, b) => {
      const byToken = a.token.localeCompare(b.token);
      if (byToken !== 0) return byToken;
      return a.captured_at.localeCompare(b.captured_at);
    });

  if (items.length === 0) return [];

  const parent = items.map((_, i) => i);
  const find = (i: number): number => {
    let root = i;
    while (parent[root] !== root) root = parent[root];
    let cur = i;
    while (parent[cur] !== cur) {
      const next = parent[cur];
      parent[cur] = root;
      cur = next;
    }
    return root;
  };
  const union = (i: number, j: number) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[ri] = rj;
  };

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (isWithinMeters(items[i], items[j], thresholdMeters)) {
        union(i, j);
      }
    }
  }

  const groups = new Map<number, PhotoAnswer[]>();
  for (let i = 0; i < items.length; i++) {
    const root = find(i);
    const list = groups.get(root) ?? [];
    list.push(items[i]);
    groups.set(root, list);
  }

  return Array.from(groups.values());
}

export type BuildPhotoMapPinsOptions = {
  photos: PhotoAnswer[];
  propertyLocation?: LatLng | null;
  nearPropertyMeters?: number;
  groupProximityMeters?: number;
};

/**
 * Build map pins for the inspection photo gallery:
 * - Photos within `nearPropertyMeters` of the property → caption on the property pin
 * - Remaining photos → clustered within `groupProximityMeters`, one pin per group
 * - No property coords → all photos clustered among themselves (no property pin)
 */
export function buildPhotoMapPins({
  photos,
  propertyLocation = null,
  nearPropertyMeters = PHOTO_NEAR_PROPERTY_METERS,
  groupProximityMeters = PHOTO_GROUP_PROXIMITY_METERS,
}: BuildPhotoMapPinsOptions): PhotoMapPin[] {
  const mappable = photosWithCoordinates(photos);
  const pins: PhotoMapPin[] = [];

  const hasProperty =
    propertyLocation != null &&
    Number.isFinite(propertyLocation.latitude) &&
    Number.isFinite(propertyLocation.longitude);

  let nearProperty: PhotoAnswer[] = [];
  let away: PhotoAnswer[] = mappable;

  if (hasProperty && propertyLocation) {
    nearProperty = [];
    away = [];
    for (const photo of mappable) {
      if (isWithinMeters(photo, propertyLocation, nearPropertyMeters)) {
        nearProperty.push(photo);
      } else {
        away.push(photo);
      }
    }

    pins.push({
      id: "property",
      kind: "property",
      latitude: propertyLocation.latitude,
      longitude: propertyLocation.longitude,
      photos: nearProperty,
      popupText:
        nearProperty.length > 0
          ? formatPhotoPinPopup(nearProperty)
          : "Property location",
    });
  }

  const groups = clusterPhotosByProximity(away, groupProximityMeters);
  groups.forEach((group, index) => {
    const center = groupCentroid(group);
    pins.push({
      id: `photo-group-${index}`,
      kind: "photo_group",
      latitude: center.latitude,
      longitude: center.longitude,
      photos: group,
      popupText: formatPhotoPinPopup(group),
    });
  });

  return pins;
}

/**
 * Whether a photo capture location is far enough from the property to warrant
 * a separate property pin on the per-photo review map.
 */
export function shouldShowSeparatePropertyPin(
  photo: LatLng,
  propertyLocation: LatLng | null | undefined,
  thresholdMeters: number = PHOTO_NEAR_PROPERTY_METERS,
): boolean {
  if (
    propertyLocation == null ||
    !Number.isFinite(propertyLocation.latitude) ||
    !Number.isFinite(propertyLocation.longitude)
  ) {
    return false;
  }
  return distanceMeters(photo, propertyLocation) > thresholdMeters;
}

/** Format a property street address for photo captions. */
export function formatPropertyAddressCaption(property: {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  display_address?: string | null;
}): string {
  if (property.display_address?.trim()) {
    return formatAddressText(property.display_address);
  }
  return formatAddressParts(property) || "Unknown address";
}
